import { ChevronLeft, Plus, useIsScrolled } from '../../../modules/design-system';
import { SLEEK_HEADER_SCROLLED, SLEEK_PILL_SURFACE, SleekButton } from '../../../modules/theme';
import { UnderlineTabs } from './UnderlineTabs';
import { HelpLink } from '../../../modules/help';
import { goToLobby, useOperatorLinks } from '../../../modules/operatorLinks';
import { LobbyPill } from '../Header/LobbyPill';

export type MyPacksTabId = 'drops' | 'deliveries';

export interface MyPacksTabDef {
  id: MyPacksTabId;
  label: string;
}

export interface MyPacksHeaderProps {
  tabs: MyPacksTabDef[];
  active: MyPacksTabId;
  onChange: (id: MyPacksTabId) => void;
  /** Left "Home" pill — returns to the game. Omitted on hosts with no game to
   *  return to (the standalone `/shipping?with_navigation` page); the left
   *  slot then stays an empty spacer so the mobile title keeps centred. */
  onHome?: () => void;
  /** Fired by the right-slot "+ Add Delivery" CTA on the Deliveries tab.
   *  Currently a placeholder — wires to the host's delivery creation flow
   *  once that endpoint lands. */
  onAddDelivery?: () => void;
  /** Fired by the right-slot "Log out" control. Only supplied for embed
   *  self-registered players (SessionContext.embedKey set); operator- and
   *  partner-provided sessions leave it undefined so the control stays hidden. */
  onLogout?: () => void;
  /** The Vault is the first screen of this load: an app tab opened
   *  `/my-packs` straight away. With a casino lobby it then closes like the
   *  lobby does, as "Home" leads into a pack page the player never came from
   *  (Betr, 2026-10-15). */
  isEntryScreen?: boolean;
}

/**
 * My Packs header — left "Home" pill (the lobby's X on an entry screen with
 * a `lobby_url`), middle tab group with custom styling
 * for the dark page (white-on-transparent idle, dark-on-white active),
 * right-slot "+ Add Delivery" CTA shown only on the Deliveries tab.
 * Layout flexes:
 *   < 600px → home + tab group stack vertically.
 *   ≥ 600px → all controls in a single horizontal row.
 */
export function MyPacksHeader({
  tabs,
  active,
  onChange,
  onHome,
  onAddDelivery,
  onLogout,
  isEntryScreen = false,
}: MyPacksHeaderProps) {
  const scrolled = useIsScrolled(8);
  const { lobbyUrl } = useOperatorLinks();
  const closeUrl = isEntryScreen ? lobbyUrl : null;
  return (
    <header
      data-co-component="MyPacksHeader"
      data-scrolled={scrolled || undefined}
      className={
        'sticky top-0 z-30 header-safe-top ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? SLEEK_HEADER_SCROLLED + ' '
          : 'border-b border-transparent ')
      }
      style={{
        fontFamily: 'var(--co-MyPacksHeader-fontFamily), Inter, system-ui, sans-serif',
      }}
    >
      {/* 20px in on a phone, like SleekPageHeader on the card / shipment
          pages and the Vault's own body: at 16 the back arrow jumped 4px
          moving between them (partner Loom 2026-10-13). */}
      <div
        className="
          mx-auto flex w-full max-w-350 flex-wrap items-center gap-3
          px-5 py-4 @[600px]:px-6 @[600px]:py-5
        "
      >
        <div className="flex flex-1 items-center justify-start">
          {closeUrl ? (
            <LobbyPill onClick={() => goToLobby(closeUrl)} />
          ) : (
            onHome && <HomeButton onClick={onHome} />
          )}
        </div>

        {/* Mobile centre-title (Figma 278:7388). Sits in its own flex
         *  slot between the back-icon and an empty right spacer so the
         *  title is geometrically centred relative to the back/spacer
         *  pair (independent of the back-icon's intrinsic width). */}
        <h1
          className="
            flex-1 text-center text-[18px] leading-[24px] font-medium tracking-[-0.44px]
            @[600px]:hidden
          "
          style={{ color: 'var(--co-Tabs-tabIdleText, #FFFFFF)' }}
        >
          Vault
        </h1>

        <div className="order-3 w-full @[600px]:order-2 @[600px]:w-auto @[600px]:flex-1">
          <UnderlineTabs tabs={tabs} active={active} onChange={onChange} />
        </div>

        <div
          className="
            order-2 flex flex-1 items-center justify-end gap-2
            @[600px]:order-3
          "
        >
          {/* Add Delivery: right-slot CTA only on ≥600px and only on the
           *  Deliveries tab. On mobile (and on the other tabs on desktop)
           *  it stays hidden. */}
          <div className="hidden @[600px]:flex">
            {active === 'deliveries' && onAddDelivery && (
              <AddDeliveryButton onClick={onAddDelivery} />
            )}
          </div>
          {/* Help: sits left of Log out, on every session type (the support
           *  site is not tied to how the player signed in). Icon-only on
           *  mobile, where the row already carries Home + tabs + Log out.
           *  Both wrappers use `contents` so the anchor is the row's own
           *  flex item — with the operator's knob off it disappears without
           *  leaving a `gap-2` hole next to Log out. */}
          <div className="contents @[600px]:hidden">
            <HelpLink variant="icon" />
          </div>
          <div className="hidden @[600px]:contents">
            <HelpLink />
          </div>
          {/* Log out: embed self-registered players only. Shown on both
           *  mobile (icon-only, mirroring the left Home icon so the centre
           *  title stays balanced) and desktop (pill), and always last.
           *  When absent the slot is an empty flex-1 spacer as before. */}
          {onLogout && <LogoutButton onClick={onLogout} />}
        </div>
      </div>
    </header>
  );
}

// Flat header pill (partner Figma 2026-10-06), as on the lobby header.
const PILL =
  SLEEK_PILL_SURFACE +
  ' inline-flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap ' +
  'font-semibold text-[14px] tracking-[-0.15px] transition-opacity hover:opacity-90';

function HomeButton({ onClick }: { onClick: () => void }) {
  return (
    <>
      {/* Mobile: the round back arrow sleek pages share. */}
      <div className="@[600px]:hidden">
        <SleekButton name="SleekBackButton" onClick={onClick} aria-label="Home">
          <ChevronLeft size={16} />
        </SleekButton>
      </div>

      {/* ≥600px: full pill with icon + "Home" label. */}
      <div className="hidden @[600px]:block">
        <button type="button" onClick={onClick} aria-label="Home" className={PILL + ' h-11 pl-3 pr-5'}>
          <ChevronLeft size={20} />
          Home
        </button>
      </div>
    </>
  );
}

function LogoutButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label="Log out" className={PILL + ' h-10 px-4 @[600px]:h-11 @[600px]:px-5'}>
      Log out
    </button>
  );
}

function AddDeliveryButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={PILL + ' h-11 pl-5 pr-4'}>
      Add Delivery
      <Plus size={20} />
    </button>
  );
}

